import { PlusOutlined, LoadingOutlined, ArrowRightOutlined, ReloadOutlined, UnlockOutlined } from '@ant-design/icons';
import {
    Alert,
    Badge,
    Button,
    Checkbox,
    Form,
    Input,
    Progress,
    Spin,
    Tag,
    Typography,
    Modal,
    List,
    Tooltip,
    notification,
    message,
} from 'antd';
import React, { CSSProperties, useState } from 'react';
import { observer } from 'mobx-react';

import { AnimatePresence, motion } from 'framer-motion';

import { PartDimensions } from './PartDimensions';
import { PartFilesHeader } from './PartPreview';
import { Price, FormErrorParser } from '@partsbadger/library';

import QuoteStore from '../stores/QuoteStore';
import { LineItemSummary } from './LineItemSummary';
import { buildSummaryPartGroups } from './Checkout/summaryGrouping';
import { QuoteLineItemProps } from '@partsbadger/types';
import QuoteLineQuantityItem from './QuoteLineQuantityItem';
import QuoteLineItem from './QuoteLineItem';
import { QuestionMark } from './QuestionMark';
import DuplicationReasonPopup from './DuplicationReasonPopup';
import { FileAnalysisAlert } from './FileAnalysisAlert';
import { useAnalysisTimeout } from '../utils/useAnalysisTimeout';
import { getDimensionsState, isBlocking3dFailure } from '../utils/partGeometry';
import { isPartEditorAvailable } from '../utils/partEditorAvailability';
import MatchedPartsModal from './MatchedPartsModal';
import { RequotePartRemovalModal } from './RequotePartRemovalModal';
import dayjs from 'dayjs';

let busy: any = null;

const { Paragraph, Text, Title, Link } = Typography;

interface HeaderTitleProps {
    item: QuoteLineItemProps;
    setLoading: (loading: boolean) => void;
    collapsed: boolean;
    primary_analysis_finished: boolean;
    isSelected: boolean;
    handleSelect: (selected: boolean) => void;
    fileActions?: React.ReactNode;
    fileUpdateLoading: boolean;
    onRequestFileUpdate: () => void;
    isRequote: boolean;
    partNumber: number;
    partCount: number;
}

// Part Header layout, normalized: checkbox + thumbnail form a compact left
// column. The content column starts with the editable name, then 3D / 2D
// file states, and finally dimensions. This makes the part name the clear
// parent of the file metadata.
const HeaderTitle = observer(
    ({
        setLoading,
        item,
        collapsed,
        primary_analysis_finished,
        isSelected,
        handleSelect,
        fileActions,
        fileUpdateLoading,
        onRequestFileUpdate,
        isRequote,
        partNumber,
        partCount,
    }: HeaderTitleProps) => {
        const { product } = item;
        const isEditable = item.master_product_details?.editable !== false;

        const displayName = product?.name?.toUpperCase() ?? '';
        const dimensions = product?.dimensions_dict;
        const dimensionStatus = {
            show: <PartDimensions {...dimensions} />,
            'no-3d': 'Unavailable — upload a 3D file to calculate them.',
            failed: 'Unavailable — the 3D file could not be analyzed.',
            pending: 'Pending — the 3D file is still being analyzed.',
            unavailable: 'Unavailable for this 3D file.',
        }[getDimensionsState(product)];

        const persistProductName = (name: string) => {
            const payload = {
                id: item.id,
                product: {
                    name,
                },
            };

            setLoading(true);

            QuoteStore.updateLineItem(payload)
                .then(() => {
                    setLoading(false);
                })
                .catch(err => {
                    console.log(err);
                    setLoading(false);
                });
        };

        return (
            <div className="flex items-start gap-3">
                <Checkbox
                    checked={isSelected}
                    data-testid="master-product-select"
                    style={{ marginTop: 13 }}
                    onClick={e => e.stopPropagation()}
                    onChange={e => handleSelect(e.target.checked)}
                />
                <PartFilesHeader
                    item={item}
                    readOnly={collapsed || !primary_analysis_finished}
                    header={
                        <div>
                            <div
                                className="text-muted"
                                style={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    letterSpacing: '0.06em',
                                    lineHeight: '16px',
                                }}
                            >
                                {`PART ${partNumber} OF ${partCount}`}
                            </div>
                            <div
                                className="product-name-wrapper flex flex-wrap items-baseline gap-2"
                                id={String(item.id)}
                            >
                                <Typography.Title
                                    level={4}
                                    className={'product-name m-0'}
                                    editable={
                                        collapsed || !isEditable
                                            ? false
                                            : {
                                                  tooltip: 'Edit Name',
                                                  onChange: persistProductName,
                                              }
                                    }
                                >
                                    {displayName}
                                </Typography.Title>
                                {item.is_requoted ? (
                                    <Tag
                                        style={{
                                            margin: 0,
                                            backgroundColor: '#8e44ad',
                                            borderColor: '#8e44ad',
                                            color: '#fff',
                                        }}
                                        data-testid="otf-requoted-tag"
                                    >
                                        Re-quoted
                                    </Tag>
                                ) : null}
                                {!collapsed && !isEditable ? (
                                    <Spin spinning={fileUpdateLoading}>
                                        <Link
                                            style={{ color: '#1890ff' }}
                                            onClick={event => {
                                                event.stopPropagation();
                                                onRequestFileUpdate();
                                            }}
                                        >
                                            <UnlockOutlined />{' '}
                                            {isRequote
                                                ? 'Replace drawing or update files while keeping part specifications.'
                                                : 'Would you like to update the files? Click here.'}
                                        </Link>
                                    </Spin>
                                ) : null}
                            </div>
                        </div>
                    }
                    footer={
                        <div className="part-preview-dimensions">
                            <span className="part-preview-dimensions-label">Dimensions:</span> {dimensionStatus}
                        </div>
                    }
                    fileActions={fileActions}
                />
            </div>
        );
    }
);

interface MasterProductProps {
    for_staff: boolean;
    item: QuoteLineItemProps;
    line_items: Array<QuoteLineItemProps>;
    partNumber: number;
    partCount: number;
    setLoading?: (loading: boolean) => void;
    isSelected: boolean;
    handleSelect: (selected: boolean) => void;
}

export const MasterProduct = observer(
    ({
        for_staff,
        item,
        line_items,
        partNumber,
        partCount,
        isSelected,
        handleSelect,
        ...props
    }: MasterProductProps) => {
        const product = item?.product;
        const includesManualQuote = line_items.some(lineItem => lineItem.needs_manual_quote);
        const selectedSubtotal = line_items.reduce((subtotal, lineItem) => {
            if (lineItem.needs_manual_quote) {
                return subtotal;
            }
            const total = Number(lineItem.total_price);
            return subtotal + (Number.isFinite(total) ? total : 0);
        }, 0);

        // Multiple-quantities variations are fanned out into sibling line items that
        // share a `quantity_group`. A master product can hold several such groups
        // (e.g. same part in two materials, each quoted at several quantities), so
        // we bucket by quantity_group and render one prices table per group. The
        // base of each group (lowest id) hosts the table; the rest are hidden.
        const isMultiQuantityItem = (li: QuoteLineItemProps) =>
            !!li.quantity_group &&
            !!li.needs_more_quantities &&
            li.master_product_details.quotable &&
            !li.has_dynamic_quantities;

        const multiQuantityGroups = new Map<string, Array<QuoteLineItemProps>>();
        (line_items ?? []).forEach(li => {
            if (isMultiQuantityItem(li)) {
                const groupId = li.quantity_group as string;
                const bucket = multiQuantityGroups.get(groupId) ?? [];
                bucket.push(li);
                multiQuantityGroups.set(groupId, bucket);
            }
        });

        const baseIdByGroup = new Map<string, number>();
        multiQuantityGroups.forEach((bucket, groupId) => {
            baseIdByGroup.set(groupId, [...bucket].sort((a, b) => a.id - b.id)[0].id);
        });

        // "Variant #N" must number only the line items that actually render a
        // QuoteLineItem card, not the raw position in `line_items`. Otherwise a
        // multi-quantity group fanned out into e.g. 8 sibling rows (only its
        // base item renders, as one merged prices table) leaves gaps in the
        // sequence — Variant #3 jumping to Variant #8 — because the hidden
        // siblings still consumed index slots 4-7.
        const visibleIndexByItemId = new Map<number, number>();
        {
            let nextVisibleIndex = 0;
            (line_items ?? []).forEach(li => {
                if (isMultiQuantityItem(li)) {
                    const groupId = li.quantity_group as string;
                    if (li.id === baseIdByGroup.get(groupId)) {
                        visibleIndexByItemId.set(li.id, nextVisibleIndex++);
                    }
                    return;
                }
                if (li.has_dynamic_quantities) {
                    return;
                }
                if (li.master_product_details.quotable) {
                    visibleIndexByItemId.set(li.id, nextVisibleIndex++);
                }
            });
        }

        // The collapsed summary must mirror the expanded view: a
        // multiple-quantities group is one variation with several price rows,
        // not one variation per generated sibling line item.
        const collapsedLineItems = (line_items ?? []).filter(li => {
            if (isMultiQuantityItem(li)) {
                return li.id === baseIdByGroup.get(li.quantity_group as string);
            }
            return !li.has_dynamic_quantities;
        });

        // The collapsed summary lists every quantity of a variation with its own price. The lines of
        // each variation come from the Quote Summary grouping (newest line per quantity, sorted).
        const quantityItemsByGroup = new Map(
            (buildSummaryPartGroups(line_items ?? [])[0]?.variants ?? []).map(variant => [variant.id, variant.items])
        );
        const quantityItemsOf = (li: QuoteLineItemProps): Array<QuoteLineItemProps> =>
            (isMultiQuantityItem(li) && quantityItemsByGroup.get(li.quantity_group as string)) || [li];

        const [loading, setLoading] = useState(false);
        const [duplicating, setDuplicating] = useState(false);
        const [previousQuotesModalVisible, setPreviousQuotesModalVisible] = useState(false);
        const legacyRequoteId =
            typeof (item.already_quote as unknown) === 'number' ? ((item.already_quote as unknown) as number) : null;

        const [confirmDeleteModalVisible, setConfirmDeleteModalVisible] = useState(false);

        const [form] = Form.useForm();

        const { analysis_status } = item?.product || {};

        const analyzer_2d_finished = analysis_status.file_2d?.completed;
        const analyzer_3d_finished = analysis_status.file_3d?.completed;
        const analyzer_2d_success = analysis_status?.file_2d?.status === 'SUCCESS';
        const analyzer_3d_success = analysis_status?.file_3d?.status === 'SUCCESS';
        const analyzer_3d_failure = isBlocking3dFailure(item?.product);
        const analyzer_2d_failure = analysis_status?.file_2d?.status === 'FAILURE';

        // The 2D analyzer enriches optional suggestions and must not block the
        // form. Only principal 3D processing controls whole-card readiness.
        const primary_analysis_finished = Boolean(analyzer_3d_finished);

        const isCollapsed = QuoteStore.collapsedLineItems.includes(item.id);

        const [showDuplicatonReasonPopup, setShowDuplicatonReasonPopup] = useState(false);

        const [filesLoading, setFilesLoading] = useState(false);

        const [duplicatingReasonType, setDuplicatingReasonType] = useState<'request_change_files' | 'request_requote'>(
            'request_change_files'
        );

        const ANALYSIS_TIMEOUT = 60000;
        const analyzerTimeout = useAnalysisTimeout(primary_analysis_finished, ANALYSIS_TIMEOUT);
        const canEditPart = isPartEditorAvailable({
            primaryAnalysisFinished: primary_analysis_finished,
            analysisTimedOut: analyzerTimeout,
            blocking3dFailure: analyzer_3d_failure,
        });

        // Get remote update on tighter tolerance
        React.useEffect(() => {
            if (item.product.tighter_tolerances) {
                form.setFieldsValue({
                    tighter_tolerances: item.product.tighter_tolerances,
                });
            }
        }, [item.product?.tighter_tolerances]);

        // React.useEffect(() => {
        //     if (analyzer_3d_finished) {
        //         QuoteStore.updateLineItem({
        //             id: item.id,
        //             quantity: item.quantity,
        //         });
        //     }
        // }, [analyzer_3d_finished]);
        //
        // React.useEffect(() => {
        //     if (analyzer_2d_finished) {
        //         QuoteStore.updateLineItem({
        //             id: item.id,
        //             quantity: item.quantity,
        //         });
        //     }
        // }, [analyzer_2d_finished]);

        const handleSubmit = async () => {
            form.validateFields()
                .then(async values => {
                    const payload = {
                        id: item.id,
                        product: {
                            tighter_tolerances: values[fieldName('tighter_tolerances')],
                            customer_notes: values[fieldName('customer_notes')],
                        },
                        master_product: {
                            tighter_tolerances: values[fieldName('tighter_tolerances')],
                        },
                        batch_shipment: values[fieldName('batch_shipment')],
                    };

                    setLoading(true);

                    QuoteStore.updateLineItem(payload).then(() => {
                        setLoading(false);
                    });
                })
                .catch(errorInfo => {
                    notification.error({
                        key: 'form-errors',
                        duration: 8,
                        message: 'Please input all required fields',
                        description: <FormErrorParser isAntd4 errors={errorInfo.errorFields} />,
                        placement: 'top',
                        maxCount: 1,
                    });
                });
        };

        const fieldName = (name: string) => {
            return name + '__' + item.id;
        };

        const handleChange = () => {
            if (busy) {
                clearTimeout(busy);
            }

            busy = setTimeout(handleSubmit, 400);
        };

        const handleReasonSelectConfirm = (reason: string) => {
            setFilesLoading(true);
            QuoteStore.duplicateMasterProduct(item.master_product_details.id, reason)
                .catch(() => {
                    message.error('Could not unlock files for update. Please try again.');
                })
                .finally(() => {
                    setFilesLoading(false);
                    setShowDuplicatonReasonPopup(false);
                });
        };

        const handleReasonSelectCancel = () => {
            setShowDuplicatonReasonPopup(false);
        };

        const requestFileReplacement = () => {
            setConfirmDeleteModalVisible(false);
            setDuplicatingReasonType('request_change_files');
            setShowDuplicatonReasonPopup(true);
        };

        const removePart = () => {
            QuoteStore.deleteMasterProduct(item.product.master_product);
            setConfirmDeleteModalVisible(false);
        };

        return (
            <>
                <RequotePartRemovalModal
                    isStaffRequote={for_staff && QuoteStore.isRequote}
                    onCancel={() => setConfirmDeleteModalVisible(false)}
                    onRemove={removePart}
                    onReplaceFiles={requestFileReplacement}
                    open={confirmDeleteModalVisible}
                />

                <DuplicationReasonPopup
                    isOpen={showDuplicatonReasonPopup}
                    partName={item.name}
                    type={duplicatingReasonType}
                    onConfirm={handleReasonSelectConfirm}
                    onCancel={handleReasonSelectCancel}
                />

                <Form form={form} colon={false}>
                    <div
                        onClick={() => {
                            if (isCollapsed) {
                                QuoteStore.setCollapsed(item.id);
                            }
                        }}
                        className={`${isCollapsed ? 'quote-product-card cursor-pointer' : 'quote-product-card'}`}
                    >
                        {for_staff && primary_analysis_finished && product?.quickturn_summary?.is_quickturn_part ? (
                            <Alert
                                type="success"
                                className="quickturn-notification"
                                showIcon={true}
                                description={
                                    <>
                                        <span>
                                            This part may be designed and purchased from our{' '}
                                            <a
                                                href="https://portal.parts-badger.com/quick-turn/design"
                                                rel="noopener noreferrer"
                                                target="_blank"
                                            >
                                                QuickTurn Portal
                                            </a>
                                        </span>
                                    </>
                                }
                            />
                        ) : null}

                        <div className="quote-product-header">
                            <div className="d-flex align-items-center">
                                <div className="flex-grow-1">
                                    <HeaderTitle
                                        item={item}
                                        setLoading={setLoading}
                                        collapsed={isCollapsed}
                                        primary_analysis_finished={primary_analysis_finished}
                                        isSelected={isSelected}
                                        handleSelect={handleSelect}
                                        partNumber={partNumber}
                                        partCount={partCount}
                                        fileUpdateLoading={filesLoading}
                                        onRequestFileUpdate={requestFileReplacement}
                                        isRequote={QuoteStore.isRequote}
                                        fileActions={
                                            item.master_product_details.quotable ? (
                                                <>
                                                    <Button
                                                        aria-label="Add New Variation"
                                                        className="add-new-variation-button"
                                                        data-action-location="header"
                                                        data-testid="add-new-variation"
                                                        icon={<PlusOutlined />}
                                                        loading={duplicating}
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            setDuplicating(true);
                                                            QuoteStore.duplicateItem(
                                                                item.product.master_product,
                                                                item.id
                                                            ).finally(() => setDuplicating(false));
                                                        }}
                                                        size="small"
                                                        type="primary"
                                                    >
                                                        Add New Variation
                                                    </Button>
                                                    <QuestionMark placement="right">
                                                        Add a part variation to see price differences
                                                    </QuestionMark>
                                                </>
                                            ) : null
                                        }
                                    />
                                </div>
                            </div>

                            <div className="product-card-actions">
                                {(product.has_file_2d && analyzer_2d_success) ||
                                (product.has_file_3d && analyzer_3d_success) ? (
                                    <AnimatePresence>
                                        <motion.span
                                            initial={{
                                                scaleY: 0,
                                                scaleX: 0,
                                            }}
                                            animate={{
                                                scaleX: 1,
                                                scaleY: 1,
                                                opacity: 1,
                                            }}
                                            exit={{
                                                scaleX: 0,
                                                scaleY: 0,
                                            }}
                                        >
                                            <Tooltip placement="top" title={isCollapsed ? 'Expand' : 'Collapse'}>
                                                <Button
                                                    type="default"
                                                    className="master-product-action-button master-product-collapse-button"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        QuoteStore.setCollapsed(item.id);
                                                    }}
                                                >
                                                    {isCollapsed ? 'Expand' : 'Collapse'}
                                                </Button>
                                            </Tooltip>
                                        </motion.span>
                                    </AnimatePresence>
                                ) : null}

                                <Tooltip placement="top" title="Delete Part">
                                    <Button
                                        type="primary"
                                        className="master-product-action-button"
                                        onClick={e => {
                                            e.stopPropagation();
                                            setConfirmDeleteModalVisible(true);
                                        }}
                                    >
                                        Delete Part
                                    </Button>
                                </Tooltip>
                            </div>
                        </div>

                        {primary_analysis_finished ? (
                            <div className="w-100">
                                <AnimatePresence>
                                    {isCollapsed ? (
                                        <motion.div
                                            initial={{
                                                height: 0,
                                                opacity: 0,
                                                scaleY: 0,
                                            }}
                                            animate={{
                                                zIndex: 1,
                                                height: 'auto',
                                                scaleY: 1,
                                                opacity: 1,
                                                transition: {
                                                    height: {
                                                        duration: 0.25,
                                                    },
                                                    opacity: {
                                                        duration: 0.25,
                                                        delay: 0,
                                                    },
                                                },
                                            }}
                                            exit={{
                                                zIndex: 0,
                                                height: 0,
                                                opacity: 0,
                                                scaleY: 0,
                                                transition: {
                                                    height: {
                                                        duration: 0.25,
                                                    },
                                                    opacity: {
                                                        duration: 0.25,
                                                    },
                                                },
                                            }}
                                        >
                                            <div className="product-collapsed-container">
                                                <List
                                                    itemLayout="horizontal"
                                                    dataSource={collapsedLineItems}
                                                    renderItem={(line_item, index) => (
                                                        <List.Item className="p-0 py-2">
                                                            <LineItemSummary
                                                                key={line_item.id}
                                                                item={line_item}
                                                                index={index}
                                                                quantityItems={quantityItemsOf(line_item)}
                                                            />
                                                        </List.Item>
                                                    )}
                                                />
                                            </div>
                                        </motion.div>
                                    ) : null}
                                </AnimatePresence>
                            </div>
                        ) : null}

                        {product.has_file_3d && analyzer_3d_failure ? (
                            <div className="pb-4">
                                <FileAnalysisAlert
                                    fileType="3D"
                                    item={item}
                                    analysisStatus={analysis_status?.file_3d}
                                />
                            </div>
                        ) : null}

                        {product.has_file_3d && !analyzer_3d_finished && !analyzerTimeout ? (
                            <div className={'loader pb-4'} data-testid="master-product-analysis-loader">
                                <div style={{ textAlign: 'center', padding: '12px 24px' }}>
                                    <LoadingOutlined style={{ fontSize: 24 }} spin />
                                    <Text className="d-block text-primary" style={{ marginTop: 8 }}>
                                        {(typeof analysis_status?.file_3d?.message === 'object' &&
                                            analysis_status?.file_3d?.message?.msg) ||
                                            'Analyzing Geometry...'}
                                    </Text>
                                    {typeof analysis_status?.file_3d?.message === 'object' &&
                                        analysis_status?.file_3d?.message != null &&
                                        analysis_status.file_3d.message.current > 0 && (
                                            <Progress
                                                percent={analysis_status.file_3d.message.current}
                                                size="small"
                                                showInfo={false}
                                                strokeColor="#1890ff"
                                                style={{ maxWidth: 200, margin: '8px auto 0' }}
                                            />
                                        )}
                                </div>
                            </div>
                        ) : canEditPart ? (
                            <React.Fragment>
                                <AnimatePresence>
                                    {!isCollapsed ? (
                                        <motion.div
                                            initial={{
                                                zIndex: 1,
                                                height: 0,
                                                opacity: 0,
                                                scaleY: 0,
                                            }}
                                            animate={{
                                                height: 'auto',
                                                opacity: 1,
                                                scaleY: 1,
                                                transition: {
                                                    height: {
                                                        duration: 0.25,
                                                    },
                                                    opacity: {
                                                        duration: 0.25,
                                                        delay: 0,
                                                    },
                                                },
                                            }}
                                            exit={{
                                                zIndex: 0,
                                                height: 0,
                                                opacity: 0,
                                                scaleY: 0,
                                                transition: {
                                                    height: {
                                                        duration: 0.25,
                                                    },
                                                    opacity: {
                                                        duration: 0.25,
                                                    },
                                                },
                                            }}
                                        >
                                            <div className={'quote-product-body'} data-testid="master-product-editor">
                                                <div className="quote-product-details">
                                                    <React.Fragment>
                                                        {line_items?.map((item, index) => {
                                                            if (isMultiQuantityItem(item)) {
                                                                const groupId = item.quantity_group as string;
                                                                // Render the group once, on its base item, as a
                                                                // single prices table. Hide the sibling variations
                                                                // (each quantity is a row in that table instead).
                                                                if (item.id !== baseIdByGroup.get(groupId)) {
                                                                    return null;
                                                                }
                                                                return (
                                                                    <QuoteLineItem
                                                                        key={item.id}
                                                                        index={
                                                                            visibleIndexByItemId.get(item.id) ?? index
                                                                        }
                                                                        item={item}
                                                                        multiQuantityItems={multiQuantityGroups.get(
                                                                            groupId
                                                                        )}
                                                                    />
                                                                );
                                                            }
                                                            if (item.has_dynamic_quantities) {
                                                                const rendered_items = item.quote_item_quantities
                                                                    .filter(i => i.displayed)
                                                                    .map((quantity_item, i) => {
                                                                        return (
                                                                            <QuoteLineQuantityItem
                                                                                key={i}
                                                                                item={{
                                                                                    quantity: quantity_item.quantity,
                                                                                    lead_time: quantity_item.lead_time,
                                                                                    unit_price: Number(
                                                                                        quantity_item.unit_price
                                                                                    ),
                                                                                    total_price: Number(
                                                                                        quantity_item.total_price
                                                                                    ),
                                                                                    safe_auto_quote:
                                                                                        quantity_item.safe_auto_quote,
                                                                                }}
                                                                            />
                                                                        );
                                                                    });

                                                                return (
                                                                    <div
                                                                        className={'quantity-items'}
                                                                        id={`otf-line-item-${item.id}`}
                                                                    >
                                                                        <Spin spinning={loading}>
                                                                            <div className={'flex flex-row'}>
                                                                                <div className={'w-full'}>
                                                                                    <div className={'flex flex-row'}>
                                                                                        <div
                                                                                            className={
                                                                                                'flex flex-row w-full'
                                                                                            }
                                                                                        >
                                                                                            <div
                                                                                                className={'w-1/4 mr-4'}
                                                                                            >
                                                                                                <div
                                                                                                    className={
                                                                                                        'flex justify-center font-semibold'
                                                                                                    }
                                                                                                >
                                                                                                    Quantity
                                                                                                </div>
                                                                                            </div>

                                                                                            <div
                                                                                                className={'w-1/4 mr-4'}
                                                                                            >
                                                                                                <div
                                                                                                    className={
                                                                                                        'flex justify-center font-semibold'
                                                                                                    }
                                                                                                >
                                                                                                    Lead Time
                                                                                                </div>
                                                                                            </div>

                                                                                            <div
                                                                                                className={'w-1/4 mr-4'}
                                                                                            >
                                                                                                <div
                                                                                                    className={
                                                                                                        'flex justify-center font-semibold'
                                                                                                    }
                                                                                                >
                                                                                                    Unit Price
                                                                                                </div>
                                                                                            </div>

                                                                                            <div
                                                                                                className={'w-1/4 mr-4'}
                                                                                            >
                                                                                                <div
                                                                                                    className={
                                                                                                        'flex justify-center font-semibold'
                                                                                                    }
                                                                                                >
                                                                                                    Total Price
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                    {rendered_items}
                                                                                </div>
                                                                            </div>
                                                                        </Spin>
                                                                        <hr />
                                                                    </div>
                                                                );
                                                            } else {
                                                                if (item.master_product_details.quotable) {
                                                                    return (
                                                                        <QuoteLineItem
                                                                            key={item.id}
                                                                            index={
                                                                                visibleIndexByItemId.get(item.id) ??
                                                                                index
                                                                            }
                                                                            item={item}
                                                                        />
                                                                    );
                                                                }
                                                            }
                                                        })}

                                                        {item.master_product_details.quotable && (
                                                            <div className="quote-product-requirements">
                                                                {for_staff &&
                                                                    !QuoteStore.isRequote &&
                                                                    item.already_quote_product &&
                                                                    item.already_quote && (
                                                                        <div className="w-100 mt-2 pb-2">
                                                                            <Spin spinning={loading}>
                                                                                <Alert
                                                                                    message={
                                                                                        <span>
                                                                                            <strong>
                                                                                                It seems that this
                                                                                                product has already been
                                                                                                quoted.
                                                                                            </strong>
                                                                                        </span>
                                                                                    }
                                                                                    description="You can request a re quote or continue to add items and submit your request."
                                                                                    action={
                                                                                        <div className="d-flex flex-column">
                                                                                            {legacyRequoteId !==
                                                                                            null ? (
                                                                                                <span
                                                                                                    style={{
                                                                                                        color:
                                                                                                            '#0f3d70',
                                                                                                        fontSize: 12,
                                                                                                        fontWeight: 600,
                                                                                                        whiteSpace:
                                                                                                            'nowrap',
                                                                                                        cursor:
                                                                                                            'pointer',
                                                                                                    }}
                                                                                                    onClick={() => {
                                                                                                        setLoading(
                                                                                                            true
                                                                                                        );
                                                                                                        QuoteStore.getRequote(
                                                                                                            legacyRequoteId
                                                                                                        ).then(
                                                                                                            (
                                                                                                                r: any
                                                                                                            ) => {
                                                                                                                location.replace(
                                                                                                                    '/get-quote/?uid=' +
                                                                                                                        r.uid
                                                                                                                );
                                                                                                                setLoading(
                                                                                                                    false
                                                                                                                );
                                                                                                            }
                                                                                                        );
                                                                                                    }}
                                                                                                >
                                                                                                    Click here to re
                                                                                                    quote!
                                                                                                </span>
                                                                                            ) : null}
                                                                                            <span
                                                                                                data-testid="otf-previously-quoted-trigger"
                                                                                                style={{
                                                                                                    color: '#0f3d70',
                                                                                                    fontSize: 12,
                                                                                                    fontWeight: 600,
                                                                                                    cursor: 'pointer',
                                                                                                }}
                                                                                                onClick={() =>
                                                                                                    setPreviousQuotesModalVisible(
                                                                                                        true
                                                                                                    )
                                                                                                }
                                                                                            >
                                                                                                View all previous quotes
                                                                                            </span>
                                                                                        </div>
                                                                                    }
                                                                                    type="warning"
                                                                                    showIcon={false}
                                                                                    className="manual-review-required-message already-quoted-alert"
                                                                                    style={{
                                                                                        width: '100%',
                                                                                        borderRadius: 8,
                                                                                    }}
                                                                                />
                                                                                <MatchedPartsModal
                                                                                    open={previousQuotesModalVisible}
                                                                                    onClose={() =>
                                                                                        setPreviousQuotesModalVisible(
                                                                                            false
                                                                                        )
                                                                                    }
                                                                                    masterProductId={
                                                                                        item.master_product_details?.id
                                                                                    }
                                                                                    currentMpId={
                                                                                        item.master_product_details?.id
                                                                                    }
                                                                                />
                                                                            </Spin>
                                                                        </div>
                                                                    )}

                                                                <div className="d-flex flex-column align-items-start">
                                                                    <>
                                                                        {!QuoteStore.isRequote && (
                                                                            <>
                                                                                <div className="d-flex flex-column">
                                                                                    {/*# TODO: its disabled for autogreen integration, please don't remove it*/}
                                                                                    {/*<Form.Item*/}
                                                                                    {/*    name={fieldName(*/}
                                                                                    {/*        'tighter_tolerances'*/}
                                                                                    {/*    )}*/}
                                                                                    {/*    rules={[*/}
                                                                                    {/*        {*/}
                                                                                    {/*            required: false,*/}
                                                                                    {/*            message:*/}
                                                                                    {/*                'Please insert a value',*/}
                                                                                    {/*        },*/}
                                                                                    {/*    ]}*/}
                                                                                    {/*    noStyle*/}
                                                                                    {/*    valuePropName={'checked'}*/}
                                                                                    {/*    initialValue={*/}
                                                                                    {/*        item.product*/}
                                                                                    {/*            ?.tighter_tolerances*/}
                                                                                    {/*    }*/}
                                                                                    {/*>*/}
                                                                                    {/*    <Checkbox*/}
                                                                                    {/*        onChange={handleChange}*/}
                                                                                    {/*        className="text-base"*/}
                                                                                    {/*        style={{ margin: 0 }}*/}
                                                                                    {/*    >*/}
                                                                                    {/*        This part has feature*/}
                                                                                    {/*        tolerance of +/- 0.001" or*/}
                                                                                    {/*        less*/}
                                                                                    {/*    </Checkbox>*/}
                                                                                    {/*</Form.Item>*/}
                                                                                </div>
                                                                            </>
                                                                        )}
                                                                    </>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {QuoteStore.isRequote && !item.master_product_details.quotable && (
                                                            <div className="quote-no-quotable-message">
                                                                <Alert
                                                                    message={'No Quoted Part'}
                                                                    description={
                                                                        <span>
                                                                            {`
                                                                                ${item.master_product_details.notes}`}
                                                                            <br />
                                                                            {
                                                                                'If you believe that modifying certain details could make the part quotable, please '
                                                                            }
                                                                            <Typography.Link
                                                                                style={{ color: '#ff7875' }}
                                                                                onClick={() => {
                                                                                    setDuplicatingReasonType(
                                                                                        'request_requote'
                                                                                    );
                                                                                    setShowDuplicatonReasonPopup(true);
                                                                                }}
                                                                            >
                                                                                click here
                                                                            </Typography.Link>

                                                                            {' to request a new requote.'}
                                                                        </span>
                                                                    }
                                                                    type="warning"
                                                                    showIcon
                                                                />
                                                            </div>
                                                        )}
                                                    </React.Fragment>
                                                </div>
                                            </div>

                                            {item.master_product_details.quotable ? (
                                                <div className="master-product-footer-actions">
                                                    <Button
                                                        aria-label="Add New Variation"
                                                        className="add-new-variation-button"
                                                        data-action-location="footer"
                                                        data-testid="add-new-variation"
                                                        icon={<PlusOutlined />}
                                                        loading={duplicating}
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            setDuplicating(true);
                                                            QuoteStore.duplicateItem(
                                                                item.product.master_product,
                                                                item.id
                                                            ).finally(() => setDuplicating(false));
                                                        }}
                                                        size="small"
                                                        type="primary"
                                                    >
                                                        Add New Variation
                                                    </Button>
                                                    <QuestionMark placement="right">
                                                        Add a part variation to see price differences
                                                    </QuestionMark>
                                                </div>
                                            ) : null}

                                            <div
                                                className={`master-product-summary${
                                                    includesManualQuote ? ' is-manual' : ' is-instant'
                                                }`}
                                                data-has-manual={includesManualQuote ? 'true' : 'false'}
                                                data-testid="master-product-summary"
                                            >
                                                <span>
                                                    {includesManualQuote
                                                        ? 'Includes manual quote'
                                                        : 'All selected prices are instant'}
                                                </span>
                                                <span data-testid="master-product-subtotal">
                                                    Selected subtotal: <Price value={selectedSubtotal} />
                                                </span>
                                            </div>
                                        </motion.div>
                                    ) : null}
                                </AnimatePresence>
                            </React.Fragment>
                        ) : null}
                    </div>
                </Form>
            </>
        );
    }
);
